import dayjs, { Dayjs } from 'dayjs';
import type { Todo } from './useData';
import type { Appointment } from '../crm-appointments/useData';

/** Effective day of month for a todo in a given year/month (1–lastDay). */
export function getEffectiveDayForMonth(todo: Todo, year: number, month: number): number {
  const raw =
    todo.todoRecurrenceDayOfMonth ??
    (todo.todoCreatedAt ? dayjs(todo.todoCreatedAt).date() : 1);
  const lastDay = dayjs()
    .year(year)
    .month(month)
    .endOf('month')
    .date();
  return Math.min(Math.max(1, raw), lastDay);
}

/** Whether a recurring todo occurs on the given day. */
export function occursOn(todo: Todo, day: Dayjs): boolean {
  if (!todo.todoRecurring || !todo.todoRecurrenceInterval) return false;
  if (todo.todoCreatedAt && day.isBefore(dayjs(todo.todoCreatedAt), 'day')) return false;

  const months = todo.todoRecurrenceInterval.months;
  const creation = todo.todoCreatedAt ? dayjs(todo.todoCreatedAt) : day;
  const creationMonth = creation.month();
  const targetDay = getEffectiveDayForMonth(todo, day.year(), day.month());

  if (day.date() !== targetDay) return false;

  switch (months) {
    case 1:
      return true;
    case 3:
      return (day.month() - creationMonth + 12) % 3 === 0;
    case 6:
      return (day.month() - creationMonth + 12) % 6 === 0;
    case 12:
      return day.month() === creationMonth;
    default:
      return false;
  }
}

/** Get todos (recurring + non-recurring due) for a given day. */
export function getTodosForDay(
  recurringTodos: Todo[],
  nonRecurringTodos: Todo[],
  day: Dayjs
): Todo[] {
  const recurring = recurringTodos.filter((t) => occursOn(t, day));
  const nonRecurring = nonRecurringTodos.filter((t) => {
    if (!t.todoDueDate) return false;
    const dueDate = dayjs(t.todoDueDate);
    return dueDate.isSame(day, 'day');
  });
  return [...recurring, ...nonRecurring];
}

/** Format appointment time for display (e.g. "14:30"). */
export function formatAppointmentTime(dateTime?: string): string {
  if (!dateTime) return '';
  const d = dayjs(dateTime);
  return d.isValid() ? d.format('HH:mm') : '';
}

/** Format appointment time range "HH:mm–HH:mm" if possible, otherwise just start time. */
export function formatAppointmentTimeRange(appointment: Appointment): string {
  const start = formatAppointmentTime(appointment.clientAppScheduleDateTime);

  let end = '';
  if (appointment.clientAppFinishDateTime) {
    end = formatAppointmentTime(appointment.clientAppFinishDateTime);
  } else if (appointment.clientAppScheduleDateTime && appointment.clientAppDurationMins) {
    const startDt = dayjs(appointment.clientAppScheduleDateTime);
    const endDt = startDt.add(appointment.clientAppDurationMins, 'minute');
    end = endDt.isValid() ? endDt.format('HH:mm') : '';
  }

  if (start && end) {
    return `${start}–${end}`;
  }
  return start;
}
